import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const initialState: FilterState = {
  pagination: {
    page: 1,
    limit: 12,
  },
  productName: "",
  filters: {
    page: 1,
    limit: 12,
    name: null,
    min_price: null,
    max_price: null,
    id_product: null,
    id_category: null,
    id_brand: null,
  },
  order: {
    order: "DESC",
    field: "price",
  },
  filterOpen: false,
  leftSidebarOpen: false,
  sidebarpopup: false,
  selectedCategory: [],
  selectedBrands: [],
  selectedPrice: {
    min: 0,
    max: 0,
  },
  filtersModified: false,
};

const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    setPagination: (
      state,
      action: PayloadAction<{ key: "page" | "limit"; value: number }>
    ) => {
      const { key, value } = action.payload;
      state.pagination[key] = value;
      state.filters[key] = value;
    },
    setProductName: (state, action: PayloadAction<string>) => {
      state.productName = action.payload;
    },
    updateFilters: (
      state,
      action: PayloadAction<{
        key: keyof Filters;
        value: string | number | number[] | null;
      }>
    ) => {
      const { key, value } = action.payload;
      // Type assertion to ensure the value matches the filter property type
      state.filters["page"] = 1;
      (state.filters[key] as Filters[keyof Filters]) =
        value && value !== "" ? value : null;
      state.filtersModified = true;

      if (key === "min_price" || key === "max_price") {
        state.selectedPrice[key === "min_price" ? "min" : "max"] =
          Number(value) || 0;
      }
    },
    findByProductName: (state, action: PayloadAction<string>) => {
      state.productName = action.payload;
      state.pagination.page = 1;
      state.filters.page = 1;
      state.filters.name = action.payload;
    },
    updateOrder: (state, action: PayloadAction<Partial<Order>>) => {
      state.order = { ...state.order, ...action.payload };
      state.filtersModified = true;
    },
    setFilterOpen: (state, action: PayloadAction<boolean>) => {
      state.filterOpen = action.payload;
    },
    setLeftSidebarOpen: (state) => {
      state.leftSidebarOpen = !state.leftSidebarOpen;
    },
    setSidebarpopup: (state, action: PayloadAction<boolean>) => {
      state.sidebarpopup = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<SelectedCategory[]>) => {
      state.selectedCategory = action.payload;
      state.pagination.page = 1;
      state.filters.page = 1;
    },
    setSelectedBrands: (state, action: PayloadAction<SelectedBrand[]>) => {
      state.selectedBrands = action.payload;
      state.pagination.page = 1;
      state.filters.page = 1;
    },
    setSelectedPrice: (
      state,
      action: PayloadAction<{ min: number; max: number }>
    ) => {
      state.selectedPrice = action.payload;
    },
    resetFilters: (state) => {
      state.productName = "";
      state.selectedCategory = [];
      state.selectedBrands = [];
      state.selectedPrice = { min: 0, max: 0 };
      state.pagination.page = 1;
      const currentPage = 1;
      const currentLimit = state.pagination.limit;

      state.filters = {
        page: currentPage,
        limit: currentLimit,
      };

      state.filtersModified = false;
    },
    initializeFromURL: (
      state,
      action: PayloadAction<{ searchParams: URLSearchParams }>
    ) => {
      const { searchParams } = action.payload;

      state.pagination = {
        page: searchParams.has("page") ? Number(searchParams.get("page")) : 1,
        limit: searchParams.has("limit")
          ? Number(searchParams.get("limit"))
          : 12,
      };

      if (searchParams.has("name")) {
        state.filters.name = searchParams.get("name") || null;
        state.productName = searchParams.get("name") || "";
      }

      if (searchParams.has("id_category")) {
        state.filters.id_category = searchParams.get("id_category")
          ? Number(searchParams.get("id_category"))
          : null;
      }

      if (searchParams.has("id_brand")) {
        state.filters.id_brand = searchParams.get("id_brand")
          ? Number(searchParams.get("id_brand"))
          : null;
      }

      if (searchParams.has("min_price")) {
        state.filters.min_price = Number(searchParams.get("min_price"));
      }

      if (searchParams.has("max_price")) {
        state.filters.max_price = Number(searchParams.get("max_price"));
      }

      state.order = {
        order: searchParams.has("order")
          ? (searchParams.get("order") as "ASC" | "DESC")
          : "DESC",
        field: searchParams.has("field")
          ? (searchParams.get("field") as string)
          : "price",
      };
    },
  },
});

export const {
  setPagination,
  setProductName,
  updateFilters,
  findByProductName,
  updateOrder,
  setFilterOpen,
  setLeftSidebarOpen,
  setSidebarpopup,
  setSelectedCategory,
  setSelectedBrands,
  setSelectedPrice,
  resetFilters,
  initializeFromURL,
} = filterSlice.actions;

export default filterSlice.reducer;
